// Board (round 2): the dispatch board's frame (DESIGN.md §8, the sample's dispatch board). One frame
// ruled above and below, holding the time scale and the lanes as one grid: the names column (150) and
// the time track (at least 720, so the day reads); grouped, the names column fits its longest name. Sideways it scrolls inside itself with the names
// held still; it never makes the page scroll sideways. Every row in it (TimeScale, DivisionGroup,
// BoardLane) takes the grid's two columns, and places its times by the span it provides (time-span.tsx).
// As it scrolls, the words of time away ("Off") that the names column or the board's edge would cut
// move to what shows of their band, or go (QA-765, edge-fit.ts); the time scale does its own hours. On
// a phone the names column is narrow (each lane's short name), so the lanes have most of the width.
// Fitting its names, the column never narrows again while the board is open: a filter that leaves
// shorter names keeps it as wide as it's been, so a lane's lines aren't cut shorter (QA-841, a sizer
// as wide as the widest it's drawn). Reads only --board-*.
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { applyFit, bandFit, clearFit, trackView } from '../edge-fit.ts';
import { TimeSpanProvider, useTimeSpan, type Density, type TimeSpan } from '../time-span.tsx';
import './Board.css';

export { spanHours, spanPct, type Density } from '../time-span.tsx';

/**
 * The names column's width to keep (QA-841): the widest drawn so far, or the first track of the grid's
 * resolved columns ("251.5px 1180px") when that's wider. 0 when there's nothing to read.
 */
export function widestNames(kept: number, columns: string): number {
  const first = Number.parseFloat(columns);
  return Number.isFinite(first) && first > kept ? first : kept;
}
/** The board's span: the one every time-placed row reads (time-span.tsx). */
export type BoardSpan = TimeSpan;
export const useBoardSpan = useTimeSpan;

export interface BoardProps {
  /** What the board is, for screen readers: "Day board, Sunday 25 October". */
  label: string;
  from?: number;
  to?: number;
  now?: number | null;
  density?: Density;
  readOnly?: boolean;
  /** "fit": the names column fits its longest name, up to a most (grouped, or arranging). */
  names?: 'fit';
  /** TimeScale first, then the lanes (or DivisionGroups holding them). */
  children: ReactNode;
}

export function Board({
  label,
  from = 7 * 60,
  to = 19 * 60,
  now = null,
  density = 'comfortable',
  readOnly = false,
  names,
  children,
}: BoardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const sizer = useRef<HTMLDivElement>(null);
  const widest = useRef(0);
  // time away's words, whole or not at all, wherever the board is scrolled to; fitting its names, the
  // column kept as wide as it's been
  const edges = useRef(() => {});
  edges.current = () => {
    const board = ref.current;
    if (!board) return;
    if (names !== 'fit') widest.current = 0;
    else if (grid.current && sizer.current) {
      widest.current = widestNames(widest.current, getComputedStyle(grid.current).gridTemplateColumns);
      sizer.current.style.inlineSize = `${widest.current}px`;
    }
    const view = trackView(board);
    for (const w of board.querySelectorAll<HTMLElement>('.fl-lane__off > span')) {
      clearFit(w);
      const band = w.parentElement!.getBoundingClientRect();
      const r = w.getBoundingClientRect();
      applyFit(w, bandFit(r, band, view, r.height / 4));
    }
  };
  useLayoutEffect(() => {
    edges.current();
  });
  useEffect(() => {
    const board = ref.current;
    if (!board) return;
    let frame = 0;
    const run = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => edges.current());
    };
    board.addEventListener('scroll', run, { passive: true });
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(run) : null;
    ro?.observe(board);
    return () => {
      cancelAnimationFrame(frame);
      board.removeEventListener('scroll', run);
      ro?.disconnect();
    };
  }, []);
  return (
    <TimeSpanProvider from={from} to={to} now={now} density={density} readOnly={readOnly}>
      <div
        ref={ref}
        className="fl-board"
        role="region"
        aria-label={`${label}. Scroll sideways for later in the day.`}
        tabIndex={0}
        data-density={density}
        data-readonly={readOnly ? '' : undefined}
        data-names={names}
      >
        <div ref={grid} className="fl-board__grid">
          {children}
          {names === 'fit' ? <div ref={sizer} className="fl-board__sizer" aria-hidden="true" /> : null}
        </div>
      </div>
    </TimeSpanProvider>
  );
}
